import { Component, computed, inject, input, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import { ReferenceFileDetailedDto, ReferenceFilesClient, ReferenceFileUpdateDto, TransitionRequestDto } from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { TextboxReadonlyControlComponent } from '@unity/forms/textbox-readonly-control/textbox-readonly-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { DownloadService } from '@unity/services/download.service';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { ButtonDownloadSharedComponent } from '@unity/shared/presentation/button-download-shared/button-download-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { EMPTY, take, tap } from 'rxjs';

class ReferenceFileUpdateSchema implements FormGroupControlSchema<ReferenceFileUpdateDto> {
  public name: FormControl<string>;

  public constructor(referenceFile?: ReferenceFileDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: referenceFile?.name });
  }
}

@Component({
  selector: 'unity-manage-reference-file-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    KENDO_SVGICON,
    KENDO_BUTTON,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxReadonlyControlComponent,
    TextboxControlComponent,
    FormLayoutComponent,
    ButtonDownloadSharedComponent,
    PanelManageSharedComponent,
    ActionDeleteSharedComponent,
    AuthorizeDirective,
    WorkflowSelectorSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-reference-file-page.component.html',
  styleUrl: './manage-reference-file-page.component.scss',
})
export class ManageReferenceFilePageComponent extends AbstractManageComponent<
  ReferenceFileDetailedDto,
  ReferenceFileUpdateDto,
  ReferenceFileUpdateSchema
> {
  private readonly downloadService = inject(DownloadService);

  private readonly referenceFilesClient = inject(ReferenceFilesClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $referenceFileId = input.required({ alias: 'referenceFileId', transform: (value: string) => parseInt(value) });
  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      const agreementId = this.$agreementId();
      return agreementId && resourceId ? { agreementId, resourceId } : undefined;
    },
    stream: ({ params: { agreementId, resourceId } }) => this.referenceFilesClient.getOneForAgreementAsDetailed(agreementId, resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<ReferenceFileUpdateSchema>) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.referenceFilesClient.updateOneForAgreement(agreementId, resourceId, form.getRawValue());
  };

  protected override deleteResourceFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.referenceFilesClient.deleteOneForAgreement(agreementId, resourceId);
  };

  protected override availableTransitionsFn$ = (resourceId: number) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.availableTransitionsClient.getAllForReferenceFileAsMini(agreementId, resourceId);
  };

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) => {
    const agreementId = this.$agreementId();
    if (!agreementId) {
      return EMPTY;
    }
    return this.transitionsClient.setForReferenceFile(agreementId, resourceId, transition);
  };

  protected override newControlsFn(dto?: ReferenceFileDetailedDto) {
    return new ReferenceFileUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'ReferenceFile:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'ReferenceFile:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'ReferenceFile:Delete';
  protected override resourceName = 'Reference File';
  protected override $resourceId = this.$referenceFileId;
  protected readonly $contentHeader = computed(() => `Reference File: ${this.$resource()?.name}`);
  protected readonly $contentHeaderSecondary = computed(() => `Agreement: ${this.$resource()?.agreement.name}`);
  protected readonly $isDownloading = signal(false);

  protected downloadFile() {
    const referenceFile = untracked(this.$resource);
    if (!referenceFile) {
      return;
    }

    this.$isDownloading.set(true);
    this.referenceFilesClient
      .downloadOneForAgreement(referenceFile.agreement.id, referenceFile.id)
      .pipe(
        take(1),
        tap(file => this.downloadService.saveFile(file))
      )
      .subscribe({
        complete: () => this.$isDownloading.set(false),
      });
  }
}
